import React, { useRef, useCallback } from "react";
import { TabListWrap, AddButton } from "./Tabs.style";
import TabButton from "./TabButton";

/**
 * TabList – role="tablist", renders TabButtons and handles arrow-key navigation.
 */
export default function TabList({
  tabs = [],
  activeKey,
  orientation = "horizontal",
  variant = "default",
  fullWidth,
  closable,
  onAdd,
  onSelect,
  onClose,
  tabClassName,
  activeTabClassName,
}) {
  const listRef = useRef(null);

  const getTabIndex = useCallback(
    (key) => tabs.findIndex((t) => t.key === key),
    [tabs]
  );
  const getPrevNext = useCallback(
    (currentKey, direction) => {
      const idx = getTabIndex(currentKey);
      if (idx === -1) return null;
      const enabled = tabs.filter((t) => !t.disabled);
      const enabledIdx = enabled.findIndex((t) => t.key === currentKey);
      if (enabledIdx === -1) return null;
      if (direction === "prev") {
        const next = enabled[enabledIdx - 1];
        return next ? next.key : null;
      }
      const next = enabled[enabledIdx + 1];
      return next ? next.key : null;
    },
    [tabs, getTabIndex]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const key = e.key;
      const isHorizontal = orientation === "horizontal";
      if (isHorizontal && (key === "ArrowLeft" || key === "ArrowRight")) {
        e.preventDefault();
        const next = key === "ArrowLeft" ? getPrevNext(activeKey, "prev") : getPrevNext(activeKey, "next");
        if (next) onSelect?.(next);
        return;
      }
      if (!isHorizontal && (key === "ArrowUp" || key === "ArrowDown")) {
        e.preventDefault();
        const next = key === "ArrowUp" ? getPrevNext(activeKey, "prev") : getPrevNext(activeKey, "next");
        if (next) onSelect?.(next);
        return;
      }
      if (key === "Home" || key === "End") {
        e.preventDefault();
        const enabled = tabs.filter((t) => !t.disabled);
        if (enabled.length === 0) return;
        const targetKey = key === "Home" ? enabled[0].key : enabled[enabled.length - 1].key;
        onSelect?.(targetKey);
      }
    },
    [orientation, activeKey, tabs, getPrevNext, onSelect]
  );

  return (
    <TabListWrap
      ref={listRef}
      role="tablist"
      aria-orientation={orientation}
      $orientation={orientation}
      $variant={variant}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => (
        <TabButton
          key={tab.key}
          tabKey={tab.key}
          label={tab.label}
          selected={activeKey === tab.key}
          disabled={tab.disabled}
          panelId={`tabpanel-${tab.key}`}
          orientation={orientation}
          variant={variant}
          fullWidth={fullWidth}
          closable={closable}
          onSelect={onSelect}
          onClose={onClose}
          className={tabClassName}
          activeTabClassName={activeTabClassName}
        />
      ))}
      {onAdd && (
        <AddButton type="button" onClick={onAdd} $fullWidth={fullWidth} aria-label="Add tab">
          +
        </AddButton>
      )}
    </TabListWrap>
  );
}
